import { useSyncExternalStore } from 'react';
import { z } from 'zod';
import { smAnswerValueSchema } from '../content/schemas';
import { type ExerciseKind, exerciseKindSchema } from '../domain/exercise';

/**
 * An exercise begun and left before "Done" — Quick math once at least one sum is answered.
 * Home shows it as in progress and the exercise resumes where it was. Core only records an
 * attempt when it is finished, so this lives on the device: `configureExerciseProgress`
 * plugs the platform's storage in at the composition root (in memory only until then).
 */
export const exerciseProgressSchema = z.object({
  kind: exerciseKindSchema,
  /** The local day it belongs to; a leftover from another day is ignored. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  startedAt: z.string(),
  /** Seconds spent before leaving, so the finished attempt counts every visit. */
  activeSeconds: z.number().int().nonnegative(),
  /** Quick math: the next sum to show, and the answers given so far. */
  index: z.number().int().nonnegative(),
  answers: z.array(smAnswerValueSchema),
  /** Quick math started from the Math library: the sheet it began on, by lineage. */
  mathSet: z.string().optional(),
  /**
   * Quick math: the sheets gone through, in order. It resumes only on the sheet it began on;
   * left without them (saved by an older version), it starts over.
   */
  sheets: z.array(z.string()).optional(),
});
export type ExerciseProgress = z.infer<typeof exerciseProgressSchema>;

export interface ExerciseProgressStorage {
  load(): string | null;
  save(serialised: string): void;
}

type ProgressByKind = Partial<Record<ExerciseKind, ExerciseProgress>>;

let storage: ExerciseProgressStorage | undefined;
let progress: ProgressByKind = {};
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function persist() {
  storage?.save(JSON.stringify(Object.values(progress)));
}

function parse(serialised: string | null): ProgressByKind {
  if (!serialised) return {};
  try {
    const entries = z.array(exerciseProgressSchema).safeParse(JSON.parse(serialised));
    if (!entries.success) return {};
    return Object.fromEntries(entries.data.map((entry) => [entry.kind, entry]));
  } catch {
    return {};
  }
}

export function configureExerciseProgress(adapter: ExerciseProgressStorage): void {
  storage = adapter;
  progress = parse(adapter.load());
  notify();
}

export function saveExerciseProgress(entry: ExerciseProgress): void {
  progress = { ...progress, [entry.kind]: entry };
  persist();
  notify();
}

export function clearExerciseProgress(kind: ExerciseKind): void {
  if (!progress[kind]) return;
  const { [kind]: _, ...rest } = progress;
  progress = rest;
  persist();
  notify();
}

/** What was left unfinished on `date`, if anything. */
export function getExerciseProgress(
  kind: ExerciseKind,
  date: string,
): ExerciseProgress | undefined {
  const entry = progress[kind];
  return entry?.date === date ? entry : undefined;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot() {
  return progress;
}

/** Every unfinished exercise on the device, by kind — check the date before trusting one. */
export function useExerciseProgress(): ProgressByKind {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
